import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Loader, TypographyV2 } from '@which/seatbelt'
import { ChevronLeftIcon, CrossIcon } from '@which/seatbelt/src/components/Icons'
import { dynamicGa4DataLayerPush } from '@which/shared'

import classnames from 'classnames'

import { useCategoryFilterQuery } from '../../../hooks/useCategoryFilterQuery/useCategoryFilterQuery'
import { useProductRecommendationsQuery } from '../../../hooks/useProductRecommendationsQuery/useProductRecommendationsQuery'
import { ShowAlternativesButton } from '../Button/ShowAlternativesButton'
import { analytics } from '../Configs/analytics'
import type { Category } from '../Configs/categories'
import { useGuidedPurchaseItemSpec } from '../hooks/useGuidedPurchaseItemSpec'
import { NoResults } from '../ResultsScreen/NoResults'
import { ResultsScreen } from '../ResultsScreen/ResultsScreen'
import { buildFilters } from '../Steps/buildFilters'
import { questionConfigsByCategory } from '../Steps/QuestionsConfig'
import type { GuidedPurchaseJourneyStep } from '../Steps/useQuestionSteps'
import { useQuestionSteps } from '../Steps/useQuestionSteps'
import styles from './GuidedPurchaseJourneyNavigation.module.scss'
import { useGuidedPurchaseNavigation } from './useGuidedPurchaseNavigation'

type GuidedPurchaseJourneyNavigationProps = {
  handleClose: () => void
  journeyType: Category
}

export const GuidedPurchaseJourneyNavigation = ({
  handleClose,
  journeyType,
}: GuidedPurchaseJourneyNavigationProps) => {
  const {
    steps: questionSteps,
    filters,
    skippedSteps,
    selectedIndexes,
    clearSelectionsFromIndex,
  } = useQuestionSteps({ category: journeyType })
  const itemSpec = useGuidedPurchaseItemSpec()
  const [isClosing, setIsClosing] = useState(false)

  const enableCAPIQuery = journeyType === 'mattresses'

  const productFilters = enableCAPIQuery
    ? buildFilters(questionConfigsByCategory[journeyType], selectedIndexes, [
        { 'category.code': 'mf' },
      ])
    : undefined

  const {
    productsWithPagination,
    totalProductCount: oldFiltersTotal,
    loading: oldFiltersLoading,
    error: oldFiltersError,
  } = useCategoryFilterQuery({
    categorySlug: journeyType,
    basicFilterData: filters,
    skip: enableCAPIQuery,
  })

  const {
    items: capiItems,
    total: capiTotal,
    loading: capiLoading,
    error: capiError,
  } = useProductRecommendationsQuery({
    filters: productFilters,
    skip: !enableCAPIQuery,
  })

  const loading = enableCAPIQuery ? capiLoading : oldFiltersLoading
  const error = enableCAPIQuery ? capiError : oldFiltersError
  const products = useMemo(
    () => (enableCAPIQuery ? capiItems : productsWithPagination?.products ?? []),
    [enableCAPIQuery, capiItems, productsWithPagination?.products]
  )
  const totalProductCount = enableCAPIQuery ? capiTotal : oldFiltersTotal

  const dialogRef = useRef<HTMLDialogElement>(null)
  useEffect(() => {
    if (dialogRef.current) {
      dialogRef.current.showModal()
    }
  }, [])

  const resultComponent = useCallback(() => {
    if (loading) {
      return null
    }

    if (error || products.length === 0) {
      return <NoResults categorySlug={journeyType} />
    }

    return <ResultsScreen categorySlug={journeyType} products={products} />
  }, [loading, error, products, journeyType])

  const resultsStep: GuidedPurchaseJourneyStep = {
    name: 'results',
    title: 'Our recommendation',
    component: resultComponent,
  }

  const goToLastPageButtonText = `See ${totalProductCount} ${journeyType}`

  const navigation = useGuidedPurchaseNavigation([...questionSteps, resultsStep], skippedSteps)

  const onNextButtonClick = () => {
    navigation.trackingCallback?.({
      item_text: 'Next',
    })
    navigation.next()
  }

  const onGoToLastPageClick = () => {
    dynamicGa4DataLayerPush({
      event: 'personalisation_tool_action',
      action_group: 'action',
      item_text: goToLastPageButtonText,
      item_spec: itemSpec,
      item_group: 'personalisation tools',
      tool_name: analytics[journeyType].tool_name,
      item_personalisation: 'false',
    })
    navigation.goToLastPage()
  }

  const handleCloseRequest = () => {
    setIsClosing(true)
  }

  const handleAnimationEnd = () => {
    if (isClosing) {
      handleClose()
    }
  }

  const handleCancel = (event: React.SyntheticEvent<HTMLDialogElement>) => {
    event.preventDefault()
    handleCloseRequest()
  }

  return (
    <dialog
      className={classnames(styles.root, { [styles.closing]: isClosing })}
      ref={dialogRef}
      onAnimationEnd={handleAnimationEnd}
      onCancel={handleCancel}
    >
      <div className={styles.topRow}>
        <button
          onClick={() => {
            if (navigation.canGoBack) {
              clearSelectionsFromIndex(navigation.currentPageIndex)
              navigation.back()
            }
          }}
          type="button"
          title="Back"
          aria-label="back"
          aria-disabled={!navigation.canGoBack}
          disabled={!navigation.canGoBack}
          className={classnames(styles.backButton, { [styles.disabled]: !navigation.canGoBack })}
        >
          <ChevronLeftIcon />
        </button>
        <TypographyV2 textStyle="sb-text-interface-heading-medium" className={styles.title}>
          {navigation.title}
        </TypographyV2>
        <button
          onClick={handleCloseRequest}
          type="button"
          title="Close"
          aria-label="close"
          className={styles.closeButton}
        >
          <CrossIcon />
        </button>
      </div>
      <div className={styles.content}>
        {navigation.component && (
          <navigation.component
            onSkip={
              navigation.isCurrentStepRequired
                ? undefined
                : () => {
                    clearSelectionsFromIndex(navigation.currentPageIndex)
                    navigation.next()
                  }
            }
          />
        )}
      </div>
      <div className={styles.bottomRow}>
        {navigation.isLastPage ? (
          !enableCAPIQuery &&
          totalProductCount > 0 && (
            <ShowAlternativesButton
              categorySlug={journeyType}
              filters={filters}
              data-item-parent-text={navigation.title}
              data-item-index="1"
              data-item-group="personalisation tools"
              data-tool-name={analytics[journeyType].tool_name}
              data-item-personalisation="false"
              data-which-id="personalisation-tool-onward-journey"
            />
          )
        ) : (
          <>
            <Button
              onClick={onGoToLastPageClick}
              appearance="secondary"
              disabled={totalProductCount === 0 && !loading}
              aria-busy={loading}
            >
              {loading ? <Loader aria-hidden="true" /> : goToLastPageButtonText}
            </Button>
            <Button
              onClick={onNextButtonClick}
              disabled={!navigation.canGoNext || navigation.isNextDisabled}
            >
              Next
            </Button>
          </>
        )}
      </div>
    </dialog>
  )
}
